/**
 * Mock Subjects, Categories, and Subject Slots
 */

import type {
  CpSubjectCategory,
  CpSubject,
  CpSubjectSlot,
  CpSubjectSlotSubject,
} from '../../../types/curriculum_planning';

const now = Date.now();

export const mockCpSubjectCategories: CpSubjectCategory[] = [
  {
    id: 'cat-core',
    name: 'Core',
    description: 'Core subjects required for all students',
    displayOrder: 1,
    createdAt: now,
  },
  {
    id: 'cat-foundation',
    name: 'Foundation',
    description: 'Foundation subjects providing broad education',
    displayOrder: 2,
    createdAt: now,
  },
  {
    id: 'cat-statutory',
    name: 'Statutory',
    description: 'Statutory subjects required by law',
    displayOrder: 3,
    createdAt: now,
  },
  {
    id: 'cat-elective',
    name: 'Elective',
    description: 'Optional subjects for student choice',
    displayOrder: 4,
    createdAt: now,
  },
];

export const mockCpSubjects: CpSubject[] = [
  // Core subjects
  {
    id: 'subj-maths',
    name: 'Mathematics',
    code: 'MATHS',
    categoryId: 'cat-core',
    requiresSpecialistRoom: false,
    maxClassSize: 30,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'subj-english',
    name: 'English',
    code: 'ENG',
    categoryId: 'cat-core',
    requiresSpecialistRoom: false,
    maxClassSize: 30,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'subj-science',
    name: 'Science',
    code: 'SCI',
    categoryId: 'cat-core',
    requiresSpecialistRoom: true,
    defaultRoomTypeId: 'rt-science',
    maxClassSize: 28,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  // Foundation subjects
  {
    id: 'subj-history',
    name: 'History',
    code: 'HIST',
    categoryId: 'cat-foundation',
    requiresSpecialistRoom: false,
    maxClassSize: 30,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'subj-geography',
    name: 'Geography',
    code: 'GEOG',
    categoryId: 'cat-foundation',
    requiresSpecialistRoom: false,
    maxClassSize: 30,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'subj-art',
    name: 'Art & Design',
    code: 'ART',
    categoryId: 'cat-foundation',
    requiresSpecialistRoom: true,
    defaultRoomTypeId: 'rt-art',
    maxClassSize: 24,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'subj-music',
    name: 'Music',
    code: 'MUS',
    categoryId: 'cat-foundation',
    requiresSpecialistRoom: true,
    defaultRoomTypeId: 'rt-music',
    maxClassSize: 30,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'subj-dt',
    name: 'Design & Technology',
    code: 'DT',
    categoryId: 'cat-foundation',
    requiresSpecialistRoom: true,
    defaultRoomTypeId: 'rt-dt',
    maxClassSize: 20,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'subj-computing',
    name: 'Computing',
    code: 'COMP',
    categoryId: 'cat-foundation',
    requiresSpecialistRoom: true,
    defaultRoomTypeId: 'rt-ict',
    maxClassSize: 30,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'subj-mfl-french',
    name: 'French',
    code: 'FRE',
    categoryId: 'cat-foundation',
    requiresSpecialistRoom: false,
    maxClassSize: 30,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'subj-mfl-spanish',
    name: 'Spanish',
    code: 'SPA',
    categoryId: 'cat-foundation',
    requiresSpecialistRoom: false,
    maxClassSize: 30,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'subj-mfl-german',
    name: 'German',
    code: 'GER',
    categoryId: 'cat-foundation',
    requiresSpecialistRoom: false,
    maxClassSize: 30,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  // Statutory subjects
  {
    id: 'subj-pe',
    name: 'Physical Education',
    code: 'PE',
    categoryId: 'cat-statutory',
    requiresSpecialistRoom: true,
    defaultRoomTypeId: 'rt-sports',
    maxClassSize: 30,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'subj-re',
    name: 'Religious Education',
    code: 'RE',
    categoryId: 'cat-statutory',
    requiresSpecialistRoom: false,
    maxClassSize: 30,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'subj-pshe',
    name: 'PSHE',
    code: 'PSHE',
    categoryId: 'cat-statutory',
    requiresSpecialistRoom: false,
    maxClassSize: 30,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  // Elective subjects (for KS4 options)
  {
    id: 'subj-business',
    name: 'Business Studies',
    code: 'BUS',
    categoryId: 'cat-elective',
    requiresSpecialistRoom: false,
    maxClassSize: 28,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'subj-drama',
    name: 'Drama',
    code: 'DRA',
    categoryId: 'cat-elective',
    requiresSpecialistRoom: true,
    defaultRoomTypeId: 'rt-drama',
    maxClassSize: 24,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'subj-food',
    name: 'Food Technology',
    code: 'FOOD',
    categoryId: 'cat-elective',
    requiresSpecialistRoom: true,
    defaultRoomTypeId: 'rt-food',
    maxClassSize: 20,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'subj-media',
    name: 'Media Studies',
    code: 'MEDIA',
    categoryId: 'cat-elective',
    requiresSpecialistRoom: false,
    maxClassSize: 28,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'subj-photography',
    name: 'Photography',
    code: 'PHOTO',
    categoryId: 'cat-elective',
    requiresSpecialistRoom: true,
    defaultRoomTypeId: 'rt-art',
    maxClassSize: 20,
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
];

export const mockCpSubjectSlots: CpSubjectSlot[] = [
  {
    id: 'slot-humanities',
    name: 'Humanities',
    code: 'HUM',
    description: 'History and Geography rotating slot',
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'slot-mfl',
    name: 'Modern Foreign Languages',
    code: 'MFL',
    description: 'Language subjects sharing timetable slot',
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'slot-creative',
    name: 'Creative Arts',
    code: 'CREATIVE',
    description: 'Art, Music, and Drama rotation',
    isActive: true,
    createdAt: now,
    updatedAt: now,
  },
];

export const mockCpSubjectSlotSubjects: CpSubjectSlotSubject[] = [
  // Humanities slot
  {
    id: 'sss-hum-hist',
    subjectSlotId: 'slot-humanities',
    subjectId: 'subj-history',
    createdAt: now,
  },
  {
    id: 'sss-hum-geog',
    subjectSlotId: 'slot-humanities',
    subjectId: 'subj-geography',
    createdAt: now,
  },
  // MFL slot
  {
    id: 'sss-mfl-fre',
    subjectSlotId: 'slot-mfl',
    subjectId: 'subj-mfl-french',
    createdAt: now,
  },
  {
    id: 'sss-mfl-spa',
    subjectSlotId: 'slot-mfl',
    subjectId: 'subj-mfl-spanish',
    createdAt: now,
  },
  {
    id: 'sss-mfl-ger',
    subjectSlotId: 'slot-mfl',
    subjectId: 'subj-mfl-german',
    createdAt: now,
  },
  // Creative slot
  {
    id: 'sss-cre-art',
    subjectSlotId: 'slot-creative',
    subjectId: 'subj-art',
    createdAt: now,
  },
  {
    id: 'sss-cre-mus',
    subjectSlotId: 'slot-creative',
    subjectId: 'subj-music',
    createdAt: now,
  },
  {
    id: 'sss-cre-dra',
    subjectSlotId: 'slot-creative',
    subjectId: 'subj-drama',
    createdAt: now,
  },
];
